import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import gsap from "gsap";

import missoesCaatinga1 from "@/assets/missoes-caatinga-1.jpeg";
import missoesCaatinga2 from "@/assets/missoes-caatinga-2b.jpg";
import missoesCaatinga3 from "@/assets/missoes-caatinga-3.jpeg";
import missoesCaatinga4 from "@/assets/missoes-caatinga-4.jpg";
import missoesCaatinga5 from "@/assets/missoes-caatinga-5.jpg";
import missoesCaatinga6 from "@/assets/missoes-caatinga-6.jpg";

type MasonryItem = {
  id: string;
  img: string;
  height: number;
  alt: string;
};

const useMedia = <T,>(queries: string[], values: T[], defaultValue: T) => {
  const get = () => {
    if (typeof window === "undefined") return defaultValue;
    const index = queries.findIndex((q) => window.matchMedia(q).matches);
    return values[index] ?? defaultValue;
  };

  const [value, setValue] = useState<T>(get);

  useEffect(() => {
    const handler = () => setValue(get());

    const mqs = queries.map((q) => window.matchMedia(q));
    mqs.forEach((mq) => mq.addEventListener("change", handler));

    return () => mqs.forEach((mq) => mq.removeEventListener("change", handler));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queries.join("|")]);

  return value;
};

const useMeasure = () => {
  const ref = useRef<HTMLDivElement | null>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });

  useLayoutEffect(() => {
    if (!ref.current) return;

    const ro = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      setSize({ width, height });
    });

    ro.observe(ref.current);
    return () => ro.disconnect();
  }, []);

  return [ref, size] as const;
};

const preloadImages = async (urls: string[]) => {
  await Promise.all(
    urls.map(
      (src) =>
        new Promise<void>((resolve) => {
          const img = new Image();
          img.src = src;
          img.onload = () => resolve();
          img.onerror = () => resolve();
        })
    )
  );
};

function getInitialPosition(
  item: { x: number; y: number },
  animateFrom: "top" | "bottom" | "center",
  viewportHeight: number
) {
  switch (animateFrom) {
    case "top":
      return { x: item.x, y: -200 };
    case "bottom":
      return { x: item.x, y: viewportHeight + 200 };
    default:
      return { x: item.x, y: item.y + 100 };
  }
}

function MasonryGrid({
  items,
  ease = "power3.out",
  duration = 0.6,
  stagger = 0.06,
  animateFrom = "bottom",
  scaleOnHover = true,
  hoverScale = 1.03,
  blurToFocus = true,
}: {
  items: MasonryItem[];
  ease?: string;
  duration?: number;
  stagger?: number;
  animateFrom?: "top" | "bottom" | "center";
  scaleOnHover?: boolean;
  hoverScale?: number;
  blurToFocus?: boolean;
}) {
  const columns = useMedia(
    ["(min-width:1500px)", "(min-width:1000px)", "(min-width:600px)", "(min-width:400px)"],
    [5, 4, 3, 2],
    1
  );

  const [containerRef, { width }] = useMeasure();
  const [imagesReady, setImagesReady] = useState(false);

  useEffect(() => {
    preloadImages(items.map((i) => i.img)).then(() => setImagesReady(true));
  }, [items]);

  const { grid, height } = useMemo(() => {
    if (!width) return { grid: [], height: 0 };

    const gap = 16;
    const colHeights = new Array(columns).fill(0);
    const columnWidth = (width - gap * (columns - 1)) / columns;

    const positioned = items.map((child) => {
      const col = colHeights.indexOf(Math.min(...colHeights));
      const x = (columnWidth + gap) * col;

      // Alturas mais equilibradas (mantém variedade sem “buracos” excessivos)
      const normalized = Math.min(1.2, Math.max(0.75, child.height / 900));
      const h = Math.round(columnWidth * normalized);

      const y = colHeights[col];
      colHeights[col] += h + gap;

      return { ...child, x, y, w: columnWidth, h };
    });

    return { grid: positioned, height: Math.max(...colHeights, 0) - gap };
  }, [columns, items, width]);

  const hasMounted = useRef(false);

  useLayoutEffect(() => {
    if (!imagesReady) return;

    const viewportHeight = typeof window !== "undefined" ? window.innerHeight : 800;

    grid.forEach((item, index) => {
      const selector = `[data-key="${item.id}"]`;

      if (!hasMounted.current) {
        const initialPos = getInitialPosition(item, animateFrom, viewportHeight);

        gsap.fromTo(
          selector,
          {
            opacity: 0,
            x: initialPos.x,
            y: initialPos.y,
            filter: blurToFocus ? "blur(10px)" : "none",
            width: item.w,
            height: item.h,
          },
          {
            opacity: 1,
            x: item.x,
            y: item.y,
            width: item.w,
            height: item.h,
            filter: "blur(0px)",
            duration: 0.8,
            ease: "power3.out",
            delay: index * stagger,
          }
        );
      } else {
        gsap.to(selector, {
          x: item.x,
          y: item.y,
          width: item.w,
          height: item.h,
          duration,
          ease,
          overwrite: "auto",
        });
      }
    });

    if (grid.length > 0) hasMounted.current = true;
  }, [grid, imagesReady, stagger, animateFrom, blurToFocus, duration, ease]);

  const handleMouseEnter = (item: MasonryItem) => {
    if (!scaleOnHover) return;
    gsap.to(`[data-key="${item.id}"]`, { scale: hoverScale, duration: 0.4, ease: "power2.out" });
  };

  const handleMouseLeave = (item: MasonryItem) => {
    gsap.to(`[data-key="${item.id}"]`, { scale: 1, duration: 0.4, ease: "power2.out" });
  };

  return (
    <div ref={containerRef} className="relative w-full" style={{ height: Math.max(height, 1) }}>
      {grid.map((item) => (
        <div
          key={item.id}
          data-key={item.id}
          className="absolute left-0 top-0 will-change-transform"
          aria-label={item.alt}
          onMouseEnter={() => handleMouseEnter(item)}
          onMouseLeave={() => handleMouseLeave(item)}
          role="img"
        >
          <div
            className="overflow-hidden rounded-2xl bg-muted shadow-[0_12px_50px_-20px_hsl(var(--foreground)/0.55)]"
            style={{ width: item.w, height: item.h }}
          >
            <img
              src={item.img}
              alt={item.alt}
              className="h-full w-full object-cover"
              loading="lazy"
            />
          </div>
        </div>
      ))}
    </div>
  );
}

const SimpleGridGallery = ({ items }: { items: MasonryItem[] }) => {
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      {items.map((item) => (
        <div key={item.id} className="group block">
          <div className="overflow-hidden rounded-2xl bg-muted shadow-[0_12px_50px_-20px_hsl(var(--foreground)/0.55)]">
            <img
              src={item.img}
              alt={item.alt}
              loading="lazy"
              width={400}
              height={256}
              className="h-64 w-full object-cover object-right transition-transform duration-300 group-hover:scale-[1.03]"
            />
          </div>
        </div>
      ))}
    </div>
  );
};

export default function MissoesCaatingaMasonryGallery({
  items,
}: {
  items?: MasonryItem[];
}) {
  const defaultItems: MasonryItem[] = [
    {
      id: "1",
      img: missoesCaatinga1,
      height: 900,
      alt: "Encontro comunitário ao ar livre",
    },
    {
      id: "2",
      img: missoesCaatinga2,
      height: 900,
      alt: "Grupo reunido em sala de encontro",
    },
    {
      id: "3",
      img: missoesCaatinga3,
      height: 900,
      alt: "Reunião com mulheres em ambiente comunitário",
    },
    {
      id: "4",
      img: missoesCaatinga4,
      height: 900,
      alt: "Momento de oração com crianças",
    },
    {
      id: "5",
      img: missoesCaatinga5,
      height: 900,
      alt: "Ação comunitária em área externa",
    },
    {
      id: "6",
      img: missoesCaatinga6,
      height: 900,
      alt: "Encontro ao ar livre com participantes sentados",
    },
  ];

  const resolvedItems = items ?? defaultItems;

  return (
    <div className="rounded-3xl border bg-card p-4 shadow-sm md:p-6">
      <SimpleGridGallery items={resolvedItems} />
    </div>
  );
}

